import React, { Fragment, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
// Hooks
import useCopyToClipboard from 'app/hooks/useCopyToClipboard';
// Types
import { pageMessages } from 'app/store/page/page.messages';
// Models
import { IPage } from '@root/models/Page';
import IInsuranceCase from 'app/models/Case';
// Store
import { useDispatch, useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
import { selectComparePagesIds, selectPageEntity } from 'app/store/page/page.selectors';
import { selectStapleById } from 'app/store/page/page.selectors';
import { selectEpisodesAll } from 'app/store/Episodes/Episodes.selectors';
import { selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
// Service
import { parsePageId } from 'app/store/page/page.service';
import { generateWorkspaceOrder } from 'app/store/page/page.service';
// MaterialUI
import { Box, Button, Theme, Tooltip, Typography } from '@mui/material';
// Icons
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
// Components
import PageImage from '@root/components/PageImage';
import Icon from 'app/components/Icon';
import DocumentName from 'app/components/DocumentName';
import { IconButton } from 'app/components/Mui';
import Highlights from 'app/components/PageImageHighlights/Highlights';
import { Bull } from 'app/components/Utilities';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  pageId: string;
};

const PageCompareListItem:React.FC<Props> = ({
  // Props
  pageId
}) => {
  const { t } = useTranslation();
  const scrollPositions = useRef<{ [key: string]: number }>({});

  const { documentId, pageNum, stapleId } = parsePageId(pageId);

  // Dispatch
  const dispatch = useDispatch();
  // State
  const comparePagesIds = useSelector(selectComparePagesIds);
  const page:IPage | undefined = useSelector((state:any) => selectPageEntity(state, { documentId, pageNum }));
  const staple:Array<IPage> | null = useSelector((state:any) => selectStapleById(state, { stapleId: stapleId || null }));
  const insuranceCase = useAppSelector(selectInsuranceCase) as IInsuranceCase;

  const toDocument = `/admin/cases/${insuranceCase?.id}/documents/${documentId}`;
  const toPage = `${toDocument}/${pageNum}`;
  const text = `${window.location.origin}${toPage}`;

  const [ asyncCopyTextToClipboardFn, copied ] = useCopyToClipboard();

  const handleClick = useCallback(async () => {
    try {
      await asyncCopyTextToClipboardFn(text);
    } catch(error){
      console.log(error);
    }
    // eslint-disable-next-line
  }, []);

  const handleRemovePageFromCompare = () => {
    dispatch(PageActions.removePageFromCompare(documentId, pageNum, stapleId));
  }

  const handleMovePageInCompare = (documentId:number | undefined, pageNum:number | undefined, isNext:boolean = false) => {
    if ( !documentId || !pageNum ) return;

    const containers = document.querySelectorAll('[id^="compare-page-item-"]');
    containers.forEach((container) => {
      const id = container.id.replace('compare-page-item-', '');
      scrollPositions.current[id] = container.scrollTop;
    });

    dispatch(PageActions.movePageInCompare(pageId, isNext))

    setTimeout(() => {
      const updatedContainers = document.querySelectorAll('[id^="compare-page-item-"]');
      updatedContainers.forEach((container) => {
        const id = container.id.replace('compare-page-item-', '');
        if (scrollPositions.current[id] !== undefined) {
          container.scrollTop = scrollPositions.current[id];
        }
      });
    }, 0);
  };

  const index = comparePagesIds.indexOf(pageId);
  const isPrevDisabled = index === 0;
  const isNextDisabled = index === comparePagesIds.length - 1;

  const buttonPrev = (
    <Button
      disabled={isPrevDisabled}
      onClick={() => handleMovePageInCompare(page?.documentId, page?.pageNum, false)}
      variant="outlined"
      color="primary"
      size="small"
    ><ChevronLeftIcon /></Button>
  );

  const buttonNext = (
    <Button
      disabled={isNextDisabled}
      onClick={() => handleMovePageInCompare(page?.documentId, page?.pageNum, true)}
      variant="outlined"
      color="primary"
      size="small"
    ><ChevronRightIcon /></Button>
  );

  const buttonRemoveFromCompare = (
    <Tooltip title={t('dialogs.pageCompare.removePage')}>
      <IconButton
        name="Remove page from compare"
        onClick={handleRemovePageFromCompare}
        color="primary"
        size="small"
      ><Icon icon="compare" /></IconButton>
    </Tooltip>
  );

  return (
    <React.Fragment>
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: '8px 0'
      }}>
        <Box
          sx={{
            textDecoration: 'none',
            color: 'rgba(0,0,0,0.6)',
            overflow: 'hidden',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
          component={Link}
          to={toDocument} target="_blank"
        ><DocumentName documentId={documentId} position="relative" /></Box>
        <Bull />
        <Box
          sx={{
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            color: 'rgba(0,0,0,0.6)',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
          component={Link}
          to={toPage} target="_blank"
        ><Typography variant="subtitle1">{t('labels.page')} {pageNum}</Typography></Box>
        <Tooltip
          sx={{
            ml: 2,
            '&:hover': {
              cursor: 'pointer',
              color: (theme:Theme) => theme.palette.primary.main
            }
          }}
          title={copied ? t('labels.copied') : t('buttons.copyLink')}
          aria-label={undefined}
        >
          <IconButton
            name="Copy page link"
            aria-label="Copy page link button"
            onClick={handleClick}
            size="small"
          >
            {copied ? (
              <CheckIcon color="success" fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      {stapleId && staple ? (
        <Fragment>
          {staple.map((page:IPage, index:number) => (
            <Fragment key={`compare-staple-item-${stapleId}-${index}`} >
              {index === 0 ? (
                <Box sx={{
                  position: 'sticky', top: 0, zIndex: 4, bgcolor: '#fff', pb: 2,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2
                }}>
                  <Box sx={{ display: 'flex', gap: 4, flexGrow: 1 }}>
                    {buttonPrev}
                    {stapleId ? (
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <Icon sx={{ mr: 2 }} icon="stapled" />
                        {t('dialogs.pageCompare.isStapled')}
                      </span>
                    ) : null}
                  </Box>
                  <ButtonWorkspace page={page} />
                  {buttonRemoveFromCompare}
                  {buttonNext}
                </Box>
              ) : null}
              <div
                style={{
                  position: 'relative',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  overflow: 'hidden'
                }}
              >
                <PageImage
                  documentId={page.documentId}
                  pageNum={page.pageNum}
                />
                <Highlights highlights={page.highlights as any} />
              </div>
            </Fragment>
          ))}
        </Fragment>
      ) : (
        <Fragment>
          <Box sx={{
            position: 'sticky', top: 0, zIndex: 4, bgcolor: '#fff', pb: 2,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2
          }}>
            <Box sx={{ flexGrow: 1 }}>
              {buttonPrev}
            </Box>
            <ButtonWorkspace page={page as IPage} />
            {buttonRemoveFromCompare}
            {buttonNext}
          </Box>
          <div style={{
            position: 'relative',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '4px',
            marginBottom: '8px',
            overflow: 'hidden'
          }}>
            <PageImage
              documentId={documentId}
              pageNum={pageNum}
            />
            <Highlights highlights={(page as any).highlights as any} />
          </div>
        </Fragment>
      )}
    </React.Fragment>
  )
}

export default PageCompareListItem;

const ButtonWorkspace = ({ page }:{ page:IPage }) => {
  const { t } = useTranslation();

  const { caseId } = useParams<{ caseId:string }>();

  const stapleId = page.staple && page.staple.order === 1 ? page.staple.id : null;

  const dispatch = useAppDispatch();

  const trialMode = useAppSelector(selectTrialMode);
  const staplePages:Array<IPage> | null = useSelector((state:any) => selectStapleById(state, { stapleId }))
  const episodes:any = useSelector(selectEpisodesAll);

  const handleClick = () => {
    const inWorkspace = !page.inWorkspace;
    const message = inWorkspace ? pageMessages.addPageToWorkspace : pageMessages.removePageFromWorkspace;
    const workspaceOrder = inWorkspace ? generateWorkspaceOrder(
      episodes, {
        parentEpisodeId: page.parentEpisodeId,
        workspaceOrder: page.workspaceOrder || null
      }
    ) : null;
    if ( staplePages && page.staple && page.staple.order === 1 ){
      const pagesToUpdate = staplePages.map((page:IPage) => ({
        caseId,
        documentId: page.documentId,
        pageNum: page.pageNum,
        version: page.version,
        inWorkspace,
        workspaceOrder
      }));
      dispatch(PageActions.patchPages(pagesToUpdate, message));
    } else {
      dispatch(PageActions.patchPage(page.documentId, page.pageNum, {
        caseId,
        version: page.version,
        inWorkspace,
        workspaceOrder
      }, message))
    }
  }

  if ( trialMode || !page ) return null;
  if ( page.staple && page.staple.order > 1 ) return null;
  return (
    <Tooltip title={page.workspaceOrder ? t('dialogs.pageCompare.removePageFromWorkspace') : t('dialogs.pageCompare.addPageToWorkspace')}>
      <IconButton
        name={page.workspaceOrder ? 'Remove page from workspace' : 'Add page to workspace'}
        onClick={handleClick}
        color={page.workspaceOrder ? 'primary' : 'default'}
        size="small"
      ><Icon icon="add_to_workspace" /></IconButton>
    </Tooltip>
  );
}

